<template>
  <div>
    <div class="ai-dialogue-check el-input-group el-input-group--append el-input-group--prepend">
      <div>
        <div class="el-input-group el-input-group--append el-input-group--prepend">
          <div class="el-input-group__prepend">{{ prepend || "Did" }}</div>
  
          <character-select
            :selected="state.sourceCharacterKey"
            :characters="getSourceCharacters(emitter.caseConf)"
            :disabled="readonly"
            :disable-selected-warning="true"
            @input="updateSourceCharacter"
          />
          <div v-if="sourceCharacterErrors.length" class="el-input-group__append">
            <help-tooltip type="error">
              <div v-for="error in sourceCharacterErrors" :key="error.message">{{ error.message }}</div>
            </help-tooltip>
          </div>
        </div>
      </div>
      <div>
        <div class="el-input-group el-input-group--append el-input-group--prepend">
          <div class="el-input-group__prepend">{{ prefix || 'say' }}</div>
          
          <el-tooltip
            :disabled="!selectedContent"
            :visible-arrow="false"
            :content="selectedContent"
            placement="top"
          >
            <el-select
              v-close-on-zoom
              :value="state.selectedItemId"
              :placeholder="placeholder"
              class="item-select"
              :disabled="readonly"
              @input="dialogueId => selectItem(dialogueId)"
            >
              <el-option
                v-for="item in getDialogueItemsForCharacter(emitter.caseConf, state.sourceCharacterKey, dialogueKey)"
                :key="item.id"
                :label="getDialogueItemContent(item).en"
                :value="item.id"
              />
            </el-select>
          </el-tooltip>
      
          <div v-if="dialogueErrors.length" class="el-input-group__append">
            <help-tooltip type="error">
              <div v-for="error in dialogueErrors" :key="error.message">{{ error.message }}</div>
            </help-tooltip>
          </div>
        </div>  
      </div>
      <div>
        <div class="el-input-group el-input-group--append el-input-group--prepend">
          <div class="operator-group">
            <div v-if="verb" class="el-input-group__append">{{ verb }}</div>
            <el-select
              id="operator-select"
              v-close-on-zoom
              :value="state.operator"
              placeholder="exactly"
              class="operator-select"
              :disabled="readonly"
              @input="operator => updateOperator(operator)"
            >
              <el-option v-for="op in operators" :key="op.value" :label="op.label" :value="op.value" />
            </el-select>
            <el-input
              v-integer-only
              type="number"
              placeholder="any amount of"
              :disabled="readonly"
              :value="state.count"
              :min="0"
              class="count-input"
              @change="value => updateCount(parseInt(value))"
            />
            <div class="el-input-group__append">times</div>
          </div>
        </div>
      </div>
      <div v-if="checkType === 'AI_QUESTION'">
        <div class="el-input-group el-input-group--append el-input-group--prepend">
          <div class="el-input-group__prepend">With confidence</div>
          <el-select
            v-close-on-zoom
            :value="state.confidenceOperator"
            class="confidence-operator-select"
            :disabled="readonly"
            @input="operator => updateConfidenceOperator(operator)"
          >
            <el-option v-for="op in confidenceOperators" :key="op.value" :label="op.label" :value="op.value" />
          </el-select>
          <el-input
            v-integer-only
            type="number"
            placeholder="Confidence"
            :disabled="readonly"
            :value="state.confidence"
            :min="1"
            :max="100"
            class="confidence-input"
            @change="value => updateConfidence(parseInt(value))"
          />
          <div v-if="confidenceErrors.length" class="el-input-group__append">
            <help-tooltip type="error">
              <div v-for="error in confidenceErrors" :key="error.message">{{ error.message }}</div>
            </help-tooltip>
          </div>
        </div>
      </div>
      <div v-if="checkType === 'AI_UNSOLICITED_DIALOGUE'">
        <div class="el-input-group el-input-group--append el-input-group--prepend">
          <div class="el-input-group__prepend">To</div>
  
          <character-select
            :selected="state.targetCharacterKey"
            :characters="getTargetCharacters(emitter.caseConf)"
            :disabled="readonly"
            :disable-selected-warning="true"
            @input="updateTargetCharacter"
          />
          <div v-if="targetCharacterErrors.length" class="el-input-group__append">
            <help-tooltip type="error">
              <div v-for="error in targetCharacterErrors" :key="error.message">{{ error.message }}</div>
            </help-tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import nodeMixins from './mixins/NodeMixins';
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import CharacterSelect from './CharacterSelect.vue';
import { getSourceCharacters, getTargetCharacters } from '~lib/common/utils/characters';
import { getDialogueItemsForCharacter } from '~lib/common/utils/dialogue';
import { isNpc } from '~lib/common/utils/npc';

export default {
  name: 'ai-dialogue-check',
  components: {
    HelpTooltip,
    CharacterSelect
  },
  mixins: [ nodeMixins ],
  props: [
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData', 
    'control',
    'checkType',
    'placeholder',
    'prefix',
    'prepend',
    'verb',
    'getDialogueItems',
    'getDialogueItemContent',
  ],
  data() {
    return {
      state: {
        sourceCharacterKey: null,
        targetCharacterKey: null,
        selectedItemId: null,
        count: null,
        operator: 'EQUAL',
        confidence: undefined,
        confidenceOperator: 'GREATER_THAN_OR_EQUAL',
        displayIndex: null,
      },
      operators: [
        { value: 'LESS_THAN', label: 'less than' },
        { value: 'EQUAL', label: 'exactly' },
        { value: 'GREATER_THAN', label: 'more than' },
      ],
      confidenceOperators: [
        { value: 'LESS_THAN', label: '<' },
        { value: 'LESS_THAN_OR_EQUAL', label: '≤' },
        { value: 'GREATER_THAN_OR_EQUAL', label: '≥' },
        { value: 'GREATER_THAN', label: '>' },
      ],
    };
  },
  computed: {
    dialogueKey() {
      return this.checkType === 'AI_UNSOLICITED_DIALOGUE' ? 'aiUnsolicited' : 'aiQuestions';
    },
    selectedContent() {
      const item = this.getSelectedItem() || {};
      const content = item ? this.getDialogueItemContent(item) : null;
      return content ? content.en : '';
    },
    sourceCharacterErrors() {
      return this.getErrorsById('missingSourceCharacter')
    },
    targetCharacterErrors() {
      return this.getErrorsById('missingTargetCharacter')
    },
    dialogueErrors() {
      return this.getErrorsByIdList(['missingDialogue', 'invalidQuestion', 'invalidUnsolicited']);
    },
    confidenceErrors() {
      return this.getErrorsByIdList(['missingConfidence', 'invalidConfidence']);
    }
  },
  created() {
    const data = this.getData(this.ikey);
    this.state.sourceCharacterKey = get(data, 'sourceCharacterKey');
    this.state.targetCharacterKey = this.resolveTargetCharacterKey(data);
  },
  mounted() {
    this.syncState();
    this.emitter.on('process', (nodeOrControl) => {
      // Update internal state when the node data changes
      // outside (e.g. from a CallAction)
      if (nodeOrControl === this.control) {
        this.syncState();
      }
    });
    this.update();
  },
  methods: {
    resolveTargetCharacterKey(data) {
      // Only unsolicited checks have a target character
      if (this.checkType !== 'AI_UNSOLICITED_DIALOGUE') {
        return get(data, 'targetCharacterKey');
      }
      if (data && 'targetCharacterKey' in data) {
        // Explicitly set: null (new node awaiting selection) or a chosen character
        return data.targetCharacterKey;
      }
      if (data && (data.id !== undefined || data.type !== undefined)) {
        // Legacy node saved before target characters existed; 
        // default to user (PROVIDER) so it doesn't error, 
        // matching AiPlayUnsolicitedEffect
        return 'user';
      }
      // Brand-new node; a target must be selected
      return null;
    },
    updateSourceCharacter(key) {
      this.state.sourceCharacterKey = key;
      // Reset selected item
      this.state.selectedItemId = null;
      const typePrefix = isNpc(this.state.sourceCharacterKey) ? 'NPC_' : '';
      const type = typePrefix + this.checkType;
      const data = { sourceCharacterKey: key, type };
      if (this.checkType === 'AI_UNSOLICITED_DIALOGUE') {
        // Carry the target through, otherwise a new node would look legacy
        // (no targetCharacterKey) and silently default to User
        data.targetCharacterKey = this.state.targetCharacterKey;
      }
      this.putData(this.ikey, data);
    },
    updateTargetCharacter(key) {
      this.state.targetCharacterKey = key;
      this.update();
    },
    selectItem(itemId) {
      this.state.selectedItemId = itemId;
      if (!this.state.confidence && this.checkType === 'AI_QUESTION') {
        this.state.confidence = 70; // Default confidence
      }
      this.update();
    },
    getSelectedItem() {
      const items = getDialogueItemsForCharacter(this.emitter.caseConf, this.state.sourceCharacterKey, this.dialogueKey);
      const item = items.find(i => i.id === this.state.selectedItemId);
      return item;
    },
    syncState() {
      const data = this.getData(this.ikey);
      const selectedItemId = get(data, 'id');
      this.state.selectedItemId = selectedItemId;
      // To make legacy checks work, if we have a saved dialogue id, set sourceCharacterKey to 'patient'
      this.state.sourceCharacterKey = get(data, 'sourceCharacterKey');
      if (selectedItemId && !this.state.sourceCharacterKey) {
        this.state.sourceCharacterKey = 'patient';
      }
      this.state.targetCharacterKey = this.resolveTargetCharacterKey(data);
      this.state.count = get(data, 'count');
      this.state.operator = get(data, 'operator') || 'EQUAL';
      this.state.confidence = get(data, 'confidence');
      this.state.confidenceOperator = get(data, 'confidenceOperator') || 'GREATER_THAN_OR_EQUAL';
      this.state.displayIndex = this.control.displayIndex;
    },
    updateCount(count) {
      if (!isNaN(count)) {
        this.state.count = count;
      } else {
        this.state.count = null;
      }
      this.update();
    },
    updateOperator(operator) {
      this.state.operator = operator;
      this.update();
    },
    updateConfidence(confidence) {
      this.state.confidence = confidence;
      this.update();
    },
    updateConfidenceOperator(operator) {
      this.state.confidenceOperator = operator;
      this.update();
    },
    update() {
      const typePrefix = isNpc(this.state.sourceCharacterKey) ? 'NPC_' : '';
      const type = typePrefix + this.checkType;
      const data = {
        sourceCharacterKey: this.state.sourceCharacterKey,
        targetCharacterKey: this.state.targetCharacterKey,
        type,
        id: this.state.selectedItemId,
        operator: this.state.operator,
        value: true,
        count: this.state.count,
        displayIndex: this.state.displayIndex,
        confidence: this.state.confidence,
        confidenceOperator: this.state.confidenceOperator,
      };
      this.putData(this.ikey, data);
      this.emitter.trigger('process', this.control);
    },
    getSourceCharacters,
    getTargetCharacters,
    getDialogueItemsForCharacter
  },
};
</script>

<style scoped>
.character-select,
.item-select {
  display: table-cell;
  width: 100%;
}

.operator-group,
.count-input, 
.count-input > .el-input__inner,
.operator-group .el-input-group__append {
  display: flex
}
.operator-group .el-input-group__append {
  align-items: center;
  width: auto !important;
}

.confidence-input {
  width: 225px;
}
.confidence-operator-select {
  width: 70px;
}
</style>